import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, ArrowLeft, Sparkles, CheckCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const Chat = () => {
  const { sparkId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spark, setSpark] = useState(null);
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef();

  useEffect(() => {
    if (!user || !sparkId) return;
    let mounted = true;

    const load = async () => {
      const { data: sd } = await supabase.from('sparks')
        .select(`*, business:businesses(*), investor:profiles!sparks_investor_id_fkey(*), entrepreneur:profiles!sparks_entrepreneur_id_fkey(*)`)
        .eq('id', sparkId).single();
      if (!sd || sd.status !== 'accepted') { navigate('/app/sparks'); return; }
      if (!mounted) return;
      setSpark(sd);
      setOther(sd.investor_id === user.id ? sd.entrepreneur : sd.investor);

      const { data: msgs } = await supabase.from('messages')
        .select('*').eq('spark_id', sparkId).order('created_at', { ascending: true });
      setMessages(msgs || []);
      setLoading(false);

      // mark received messages as read
      await supabase.from('messages').update({ read: true })
        .eq('spark_id', sparkId).neq('sender_id', user.id).eq('read', false);
    };
    load();

    const ch = supabase.channel(`chat-${sparkId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `spark_id=eq.${sparkId}` }, (payload) => {
        if (mounted) setMessages((p) => [...p, payload.new]);
      })
      .subscribe();

    return () => { mounted = false; supabase.removeChannel(ch); };
  }, [user, sparkId, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !user) return;
    const content = text.trim();
    setText('');
    await supabase.from('messages').insert({ spark_id: sparkId, sender_id: user.id, content });
  };

  if (loading) return <div className="py-20 text-center text-slate-400 text-sm">Carregando...</div>;

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-6 py-4">
      <div className="flex flex-col h-[calc(100vh-180px)] glass-card rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3" style={{ background:'linear-gradient(180deg, rgba(20,25,50,0.4), transparent)' }}>
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><ArrowLeft size={16}/></button>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center font-bold text-sm">
            {other?.avatar_url ? <img src={other.avatar_url} alt="" className="w-full h-full object-cover"/> : (other?.full_name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{other?.full_name}</div>
            <div className="text-[10px] text-slate-400 truncate">{spark?.business?.name}</div>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-bold text-[#ffd54f] bg-[#ffa726]/15 border border-[#ffa726]/30 rounded-full px-2 py-1">
            <Sparkles size={10}/> Spark
          </div>
        </div>

        {/* Banner */}
        <div className="mx-4 mt-3 rounded-2xl px-4 py-2.5 text-center" style={{
          background: 'linear-gradient(135deg, rgba(255,167,38,0.12), rgba(79,172,254,0.10))',
          border: '1px solid rgba(255,167,38,0.2)',
        }}>
          <div className="text-[10px] uppercase tracking-widest text-[#ffd54f] font-semibold">⚡ Spark realizada ⚡</div>
          <div className="text-xs text-slate-300 mt-0.5">Uma nova oportunidade começou. Conversem com carinho 💛</div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {messages.length === 0 && <div className="text-center text-xs text-slate-500 py-12">Comece a conversa 👋</div>}
          {messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <motion.div key={m.id} initial={{opacity:0, y:6}} animate={{opacity:1,y:0}} className={`flex ${mine?'justify-end':'justify-start'}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${mine ? 'rounded-br-md text-white' : 'rounded-bl-md bg-white/[0.06] text-white border border-white/10'}`}
                  style={mine ? { background:'linear-gradient(135deg, #4facfe, #1e90ff)', boxShadow:'0 4px 14px rgba(79,172,254,0.3)' } : {}}>
                  <div className="text-sm leading-relaxed">{m.content}</div>
                  <div className={`flex items-center justify-end gap-1 mt-1 text-[9px] ${mine?'text-white/75':'text-slate-400'}`}>
                    {new Date(m.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    {mine && <CheckCheck size={11}/>}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-white/10 px-3 py-3 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Mande uma mensagem..."
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50" />
          <button type="submit" disabled={!text.trim()} className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-50" style={{background:'linear-gradient(135deg, #4facfe, #1e90ff)', boxShadow:'0 4px 14px rgba(79,172,254,0.4)'}}>
            <Send size={14}/>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
